import { Component, OnInit } from '@angular/core';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import * as alertifyjs from 'alertifyjs';

interface ApprovalPolicyData {
    mode: string | null;
    rotationState: { lastWinner?: string; lastRotationAt?: string } | null;
    updatedBy: string | null;
    updatedAt: string | null;
    createdAt: string | null;
    availableModes: string[];
}

@Component({
    selector: 'app-approval-policy-mode',
    templateUrl: './approval-policy-mode.component.html',
    styleUrls: ['./approval-policy-mode.component.css']
})
export class ApprovalPolicyModeComponent implements OnInit {

    policyData: ApprovalPolicyData | null = null;
    selectedMode: string = '';

    policyLoading = false;
    policySaving = false;
    policyNotSeeded = false;
    policyError: string | null = null;

    constructor(private appConfigService: AppConfigService) {}

    ngOnInit(): void {
        this.loadPolicy();
    }

    async loadPolicy(): Promise<void> {
        this.policyLoading = true;
        this.policyError = null;
        try {
            const res = await this.appConfigService.getApprovalPolicy();
            this.policyData = res?.data || res;
            this.selectedMode = this.policyData?.mode || '';
            this.policyNotSeeded = false;
        } catch (error: any) {
            if (error?.status === 404) {
                this.policyNotSeeded = true;
                this.policyData = null;
            } else {
                this.policyError = error?.error?.message || error?.message || 'Error desconocido';
                alertifyjs.error(`Error cargando política: ${this.policyError}`);
            }
        } finally {
            this.policyLoading = false;
        }
    }

    async savePolicyMode(): Promise<void> {
        if (!this.selectedMode || this.selectedMode === this.policyData?.mode) return;
        const confirmed = window.confirm(
            `¿Confirmar cambio a modo ${this.getPolicyModeLabel(this.selectedMode)}? Esta acción queda registrada en auditoría.`
        );
        if (!confirmed) return;

        this.policySaving = true;
        try {
            const res = await this.appConfigService.updateApprovalPolicyMode(this.selectedMode);
            if (res?.data?.unchanged) {
                alertifyjs.message('El modo ya era el solicitado.');
            } else {
                alertifyjs.success('Modo de política actualizado');
            }
            await this.loadPolicy();
        } catch (error: any) {
            alertifyjs.error(error?.error?.message || error?.message || 'Error al guardar');
        } finally {
            this.policySaving = false;
        }
    }

    getPolicyModeLabel(mode: string | null | undefined): string {
        const map: Record<string, string> = {
            ALTERNATING_ROTATION: 'Rotación alternada',
            FIXED_MT: 'Fijo MT',
            FIXED_CREDOLAB: 'Fijo Credolab',
            REQUIRE_BOTH: 'Requiere ambos',
            SPLIT_50_50_HASH: 'Split 50/50 (hash)',
        };
        return (mode && map[mode]) || mode || '—';
    }

    getPolicyModeDescription(mode: string): string {
        const map: Record<string, string> = {
            ALTERNATING_ROTATION: 'Alterna entre MT y Credolab 1-a-1. Cada decisión cambia el próximo ganador. Útil para A/B testing sostenido.',
            FIXED_MT: 'Siempre gana MT Score. Credolab se evalúa pero sólo queda como histórico (no decisivo).',
            FIXED_CREDOLAB: 'Siempre gana Credolab. MT se evalúa pero sólo queda como histórico.',
            REQUIRE_BOTH: 'APPROVED sólo si los dos aprueban. Si cualquiera rechaza, REFUSED.',
            SPLIT_50_50_HASH: 'Reparto 50/50 determinístico por hash del codePerson. El mismo usuario siempre cae en el mismo handler.',
        };
        return map[mode] || '';
    }

    getNextRotationWinner(): string | null {
        if (this.policyData?.mode !== 'ALTERNATING_ROTATION') return null;
        const last = this.policyData?.rotationState?.lastWinner;
        if (!last) return null;
        return last === 'MT' ? 'CREDOLAB' : 'MT';
    }
}
